"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { discardPendingVisitorToken, hasPendingVisitorToken, restorePendingVisitorToken, takePendingVisitorToken } from "@/lib/identity/capture";
import { buildPartyIdentificationEvent, type PartyIdentificationEvent } from "@/lib/identity/party-identification";
import { CONTACT_ID_PATTERN, isVisitorTokenEnabled } from "@/lib/identity/shared";
import { useConsent } from "./ConsentProvider";

/**
 * Email-link visitor identification (`?aqt=<token>`), behind NEXT_PUBLIC_VISITOR_TOKEN.
 *
 * Privacy contract (see the PR's "Privacy risk note"):
 *  1. The token is read from the URL and removed with history.replaceState by
 *     src/instrumentation-client.ts, which Next.js runs before React hydration —
 *     before ThirdPartyScripts can render a single GTM / GA4 / Salesforce beacon
 *     tag — so none of them ever sees it. Every other query param (UTMs, the
 *     `?email=` prefill, ...) is left untouched.
 *  2. It is held in module memory only (see lib/identity/capture.ts): never in
 *     cookies, localStorage, sessionStorage, IndexedDB or the dataLayer. Closing or
 *     reloading the page discards it, which is also what happens if consent is
 *     never granted.
 *  3. Only once the visitor has granted consent AND the Salesforce SDK has been
 *     initialized is it sent to /api/visitor/identify. The server verifies the
 *     signature and returns the opaque Contact Id, which is sent to Data Cloud as
 *     a single partyIdentification event per page load. Never an email address.
 *
 * This hook is self-contained: it needs only the consent context and the SDK on
 * `window`, so it does not depend on how ThirdPartyScripts tracks its own state.
 */

const IDENTIFY_ENDPOINT = "/api/visitor/identify";
const READY_POLL_INTERVAL_MS = 500;
// The beacon is injected lazyOnload and can be blocked outright (ad blockers);
// wait generously for it, then drop the token.
const READY_TIMEOUT_MS = 5 * 60_000;
const REQUEST_TIMEOUT_MS = 8_000;
// Bounded retries for a transient route failure (cold start, 5xx, 408/429, a hung
// or dropped connection), because the token is gone from the URL and cannot be
// recovered by a reload.
const RETRY_DELAYS_MS = [2_000, 8_000] as const;
const RETRYABLE_STATUSES = new Set([408, 425, 429]);
let identificationStarted = false;

/**
 * True once SalesforceInteractions.init() has run: the SDK only mints (or loads)
 * its anonymous id there, and its init body is synchronous, so a non-empty id
 * means the SDK is ready to accept events.
 */
function isSdkInitialized(): boolean {
  const sdk = window.SalesforceInteractions as unknown as {
    getAnonymousId?: () => string | undefined;
  } | undefined;
  try {
    return Boolean(sdk?.getAnonymousId?.());
  } catch {
    return false;
  }
}
function sendPartyIdentification(contactId: string): void {
  // The SDK accepts profile events with no `interaction`; the shared event type
  // marks it required, so address the SDK through a narrow local view.
  const sdk = window.SalesforceInteractions as unknown as {
    sendEvent: (event: PartyIdentificationEvent) => void;
  } | undefined;
  try {
    sdk?.sendEvent(buildPartyIdentificationEvent(contactId));
  } catch {
    // Identification is best-effort; a rejected event must never surface as
    // an unhandled rejection or affect the page.
  }
}
function wait(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

/** Returns the verified Contact Id, null for a definitive "no", undefined for "try again". */
async function requestContactId(token: string): Promise<string | null | undefined> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(IDENTIFY_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        token
      }),
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal
    });
    if (response.status >= 500 || RETRYABLE_STATUSES.has(response.status)) return undefined;
    if (response.status !== 200) return null;
    const body = (await response.json()) as {
      contactId?: unknown;
    };
    return typeof body.contactId === "string" && CONTACT_ID_PATTERN.test(body.contactId) ? body.contactId : null;
  } catch {
    return undefined;
  } finally {
    window.clearTimeout(timeout);
  }
}
async function identify(isConsentGranted: () => boolean): Promise<void> {
  if (identificationStarted) return;
  const token = takePendingVisitorToken();
  if (!token) return;
  identificationStarted = true;
  let contactId: string | null | undefined;
  for (let attempt = 0;; attempt++) {
    contactId = await requestContactId(token);
    if (contactId !== undefined || attempt >= RETRY_DELAYS_MS.length) break;
    await wait(RETRY_DELAYS_MS[attempt]);
    if (!isConsentGranted()) break;
  }
  if (!isConsentGranted()) {
    // Consent was withdrawn meanwhile: send nothing, keep the token (in memory)
    // so a re-grant later in this same page load can still identify the visitor.
    restorePendingVisitorToken(token);
    identificationStarted = false;
    return;
  }
  if (contactId) sendPartyIdentification(contactId);
}

/**
 * Sends the partyIdentification event for an email-link visitor, once per page
 * load, after consent is granted and the SDK is initialized.
 */
export function useVisitorToken() {
  const $ = _c(6);
  const {
    marketingConsent,
    analyticsConsent
  } = useConsent();
  const granted = marketingConsent === "granted" || analyticsConsent === "granted";
  const grantedRef = useRef(granted);
  let t0;
  let t1;
  if ($[0] !== granted) {
    t0 = () => {
      grantedRef.current = granted;
    };
    t1 = [granted];
    $[0] = granted;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== granted) {
    t2 = () => {
      if (!isVisitorTokenEnabled() || !granted || identificationStarted || !hasPendingVisitorToken()) {
        return;
      }
      const startedAt = Date.now();
      const timer = window.setInterval(() => tick(), READY_POLL_INTERVAL_MS);
      function tick() {
        if (isSdkInitialized()) {
          window.clearInterval(timer);
          identify(() => grantedRef.current);
        } else {
          if (Date.now() - startedAt > READY_TIMEOUT_MS) {
            window.clearInterval(timer);
            discardPendingVisitorToken();
          }
        }
      }
      tick();
      return () => window.clearInterval(timer);
    };
    t3 = [granted];
    $[3] = granted;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
}